import Seo from "@/components/layout/Seo";
import JsonLd from "@/components/layout/JsonLd";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import SectionCTA from "@/components/marketing/SectionCTA";
import InlineCTA from "@/components/marketing/InlineCTA";
import PrestationsCrossLinks from "@/components/marketing/PrestationsCrossLinks";
import Reveal from "@/components/layout/Reveal";
import { HeartHandshake, Sparkles, Ruler } from "lucide-react";
import { buildBreadcrumb } from "@/lib/schema";

export default function EntreprisePage() {
    return (
        <>
            <Seo
                title="Entreprise de terrassement à Genève | RCPS Terrassement Sàrl"
                description="RCPS Terrassement Sàrl est une entreprise basée à Plan-les-Ouates, active à Genève et en Suisse romande dans le terrassement, la démolition, le génie civil, la voirie, les enrobés, les aménagements extérieurs et l’enrochement."
            />
            <JsonLd
                data={buildBreadcrumb([
                    { name: "Accueil", path: "/" },
                    { name: "Entreprise", path: "/entreprise" },
                ])}
                id="breadcrumb-entreprise"
            />
            <PageHero
                kicker="L’entreprise"
                h1="Entreprise de terrassement, démolition et génie civil à Genève"
                lead="Une entreprise de terrassement ancrée à Genève, active en Suisse romande."
                paragraphs={[
                    "Basée à Plan-les-Ouates, RCPS Terrassement Sàrl accompagne les particuliers, architectes, régies, promoteurs, entreprises générales, collectivités et professionnels dans leurs travaux de terrassement, démolition, génie civil, voirie, enrobés, aménagements extérieurs et enrochement.",
                    "Nous intervenons à Genève, dans l’ensemble du canton de Genève et dans toute la Suisse romande, avec une approche de terrain fondée sur l’analyse du chantier, la clarté des échanges et une exécution soignée.",
                ]}
                image="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=2400&q=80"
            />

            {/* Presentation */}
            <section className="bg-white" data-testid="entreprise-presentation">
                <div className="rcps-container pt-10 md:pt-14">
                    <Breadcrumbs
                        trail={[
                            { name: "Accueil", path: "/" },
                            { name: "Entreprise", path: "/entreprise" },
                        ]}
                    />
                </div>
                <div className="rcps-container py-20 md:py-28 grid md:grid-cols-12 gap-10 md:gap-14 items-center">
                    <div className="md:col-span-7 order-2 md:order-1">
                        <Reveal>
                            <p className="kicker text-navy-700"><span className="hairline" /> Présentation</p>
                            <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-700 tracking-tight leading-[1.1]">
                                Un savoir-faire complet pour vos travaux de terrain.
                            </h2>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <p className="mt-8 text-navy-700/80 text-base md:text-lg leading-[1.8]">
                                RCPS Terrassement Sàrl est une entreprise suisse spécialisée dans les travaux de
                                terrassement, démolition, déconstruction, génie civil, voirie, enrobés, aménagements
                                extérieurs et enrochement. Depuis Plan-les-Ouates, nous accompagnons des projets à
                                Genève et en Suisse romande, pour des clients privés, professionnels et
                                institutionnels.
                            </p>
                            <p className="mt-5 text-navy-700/80 text-base md:text-lg leading-[1.8]">
                                Notre rôle est d’intervenir sur les étapes essentielles qui préparent, transforment
                                ou structurent un terrain : fouilles, excavation, décaissement, évacuation des
                                matériaux, démolition partielle, préparation d’accès, réseaux, surfaces
                                carrossables, soutènements, talus et finitions extérieures.
                            </p>
                            <p className="mt-5 text-navy-700/80 text-base md:text-lg leading-[1.8]">
                                Chaque chantier est étudié selon sa localisation, ses accès, la nature du sol, les
                                volumes à déplacer ou évacuer, les contraintes de voisinage, les réseaux existants,
                                les délais et les éventuelles démarches nécessaires. Cette analyse permet de
                                proposer une intervention adaptée, réaliste et cohérente avec les contraintes du
                                projet.
                            </p>
                        </Reveal>
                    </div>
                    <div className="md:col-span-5 order-1 md:order-2">
                        <Reveal delay={0.05}>
                            <div className="relative rounded-md overflow-hidden border border-concrete-200 aspect-[4/5] group">
                                <img
                                    src="https://images.unsplash.com/photo-1757584666096-59deb41f1124?auto=format&fit=crop&w=1400&q=80"
                                    alt="Genève, Suisse — Jet d'eau et lac Léman"
                                    className="w-full h-full object-cover transition duration-[900ms] group-hover:scale-105"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/70 via-transparent to-transparent" />
                                <div className="absolute bottom-5 left-5 right-5 text-white">
                                    <p className="font-mono-num text-[10px] uppercase tracking-[0.18em] text-white/80">
                                        Notre ville · Genève
                                    </p>
                                    <p className="mt-2 font-heading text-lg md:text-xl font-semibold leading-tight">
                                        Une entreprise ancrée sur son territoire.
                                    </p>
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <InlineCTA
                variant="navy"
                title="Parlez-nous de votre projet."
                text="Chaque chantier commence par un échange. Décrivez-nous votre projet et nous revenons vers vous avec une analyse claire."
                primary={{ label: "Demander un devis", to: "/contact" }}
                secondary={{ label: "Nos prestations", to: "/prestations/terrassement" }}
                dataTestId="entreprise-inline-cta"
            />

            {/* Approche */}
            <section className="bg-concrete-50 border-y border-concrete-200" data-testid="entreprise-approche">
                <div className="rcps-container py-20 md:py-28">
                    <div className="grid md:grid-cols-12 gap-6 items-end mb-10">
                        <div className="md:col-span-8">
                            <Reveal>
                                <p className="kicker text-navy-700"><span className="hairline" /> Notre approche</p>
                                <h2 className="mt-4 font-heading text-3xl md:text-5xl font-semibold text-navy-700 tracking-tight">
                                    Méthode, précision et réactivité sur chaque chantier.
                                </h2>
                            </Reveal>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-5">
                        {[
                            {
                                i: <Ruler className="w-5 h-5" />,
                                t: "Étude & préparation",
                                d: "Avant chaque intervention, nous analysons la demande, la localisation du chantier, les accès, les contraintes techniques, les volumes et les prestations nécessaires.",
                            },
                            {
                                i: <HeartHandshake className="w-5 h-5" />,
                                t: "Un suivi clair",
                                d: "Nous privilégions des échanges simples et structurés afin de faciliter la coordination entre le client, les éventuels mandataires et les autres intervenants du chantier.",
                            },
                            {
                                i: <Sparkles className="w-5 h-5" />,
                                t: "Finitions & remise en ordre",
                                d: "Les travaux sont réalisés avec attention jusqu’à la remise en ordre de la zone d’intervention, selon les prestations prévues et les contraintes du site.",
                            },
                        ].map((x, i) => (
                            <Reveal key={x.t} delay={i * 0.05}>
                                <div className="h-full bg-white rounded-md border border-concrete-200 p-7" data-testid={`approche-card-${i + 1}`}>
                                    <div className="w-11 h-11 rounded-md bg-concrete-100 text-navy-700 flex items-center justify-center">{x.i}</div>
                                    <p className="mt-6 font-mono-num text-xs text-steel">{String(i + 1).padStart(2, "0")}</p>
                                    <h3 className="mt-2 font-heading text-xl font-semibold text-navy-700 tracking-tight">{x.t}</h3>
                                    <p className="mt-2 text-sm text-navy-700/70 leading-relaxed">{x.d}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Engagements */}
            <section className="relative bg-navy-700 text-white overflow-hidden" data-testid="entreprise-engagements">
                <div className="absolute inset-0 bg-topo-dark opacity-70 pointer-events-none" />
                <div className="absolute inset-0 noise-overlay pointer-events-none" />
                <div className="relative rcps-container py-20 md:py-28 grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <Reveal>
                            <p className="kicker text-white/70"><span className="hairline bg-white/60" /> Nos engagements</p>
                            <h2 className="mt-4 font-heading text-3xl md:text-5xl font-semibold tracking-tight">
                                Des engagements clairs pour vos travaux à Genève et en Suisse romande.
                            </h2>
                        </Reveal>
                    </div>
                    <div className="md:col-span-7 grid sm:grid-cols-2 gap-4">
                        {[
                            {
                                t: "Devis personnalisé",
                                d: "Chaque demande est étudiée selon la nature du chantier, les accès, les surfaces, les volumes, les matériaux, la localisation et les contraintes techniques.",
                            },
                            {
                                t: "Sécurité du chantier",
                                d: "La sécurité des personnes, des accès, des ouvrages existants, des réseaux et de l’environnement immédiat est prise en compte dans l’organisation des travaux.",
                            },
                            {
                                t: "Planning clair",
                                d: "Les étapes sont organisées afin de faciliter la coordination du chantier et de limiter les imprévus liés à l’intervention.",
                            },
                            {
                                t: "Qualité d’exécution",
                                d: "Nous accordons une attention particulière à la préparation du terrain, à la gestion des niveaux, aux évacuations, aux accès et aux finitions.",
                            },
                            {
                                t: "Solutions adaptées",
                                d: "Les prestations sont ajustées selon la configuration du terrain, l’usage prévu, les contraintes logistiques et les besoins du client.",
                            },
                            {
                                t: "Chantier organisé",
                                d: "Nous veillons à une intervention structurée, avec une gestion adaptée des matériaux, des gravats, des accès et de la remise en ordre.",
                            },
                        ].map((x, i) => (
                            <Reveal key={x.t} delay={i * 0.04}>
                                <div className="rounded-md border border-white/12 bg-white/[0.03] p-5 h-full" data-testid={`engagement-card-${i + 1}`}>
                                    <p className="font-mono-num text-white/50 text-xs">{String(i + 1).padStart(2, "0")}</p>
                                    <h3 className="mt-2 font-heading text-lg font-semibold">{x.t}</h3>
                                    <p className="mt-2 text-sm text-white/70 leading-relaxed">{x.d}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Zone intervention */}
            <section className="bg-white" data-testid="entreprise-zone">
                <div className="rcps-container py-20 md:py-28 grid md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-6">
                        <Reveal>
                            <div className="rounded-md overflow-hidden border border-concrete-200">
                                <img
                                    src="/rcps-assets/zone-intervention.jpeg"
                                    alt="Zone d'intervention — Genève et Suisse romande"
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            </div>
                        </Reveal>
                    </div>
                    <div className="md:col-span-6">
                        <Reveal delay={0.1}>
                            <p className="kicker text-navy-700"><span className="hairline" /> Zone d’intervention</p>
                            <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold text-navy-700 tracking-tight">
                                Basés à Genève, actifs dans toute la Suisse romande.
                            </h2>
                            <p className="mt-5 text-navy-700/80 leading-relaxed">
                                RCPS Terrassement Sàrl intervient à Genève, dans l’ensemble du canton de Genève et
                                dans toute la Suisse romande pour les projets de terrassement, démolition, génie
                                civil, voirie, enrobés, aménagements extérieurs et enrochement.
                            </p>
                            <p className="mt-4 text-navy-700/80 leading-relaxed">
                                Nous étudions les demandes à Genève, Plan-les-Ouates, Carouge, Lancy, Vernier,
                                Meyrin, Onex, Nyon, La Côte, Lausanne, dans le canton de Vaud, à Fribourg,
                                Neuchâtel, dans le Jura et en Valais romand, selon la nature des travaux, les accès
                                au chantier, les volumes à évacuer et les contraintes logistiques.
                            </p>
                            <div className="mt-6 flex flex-wrap gap-2" data-testid="zone-tags">
                                {[
                                    "Genève",
                                    "Plan-les-Ouates",
                                    "Carouge",
                                    "Lancy",
                                    "Vernier",
                                    "Meyrin",
                                    "Nyon",
                                    "Lausanne",
                                    "Vaud",
                                    "Fribourg",
                                    "Neuchâtel",
                                    "Jura",
                                    "Valais romand",
                                    "Suisse romande",
                                ].map((z) => (
                                    <span
                                        key={z}
                                        className="px-3 py-1.5 rounded border border-concrete-200 text-navy-700 text-xs font-medium bg-white"
                                    >
                                        {z}
                                    </span>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <PrestationsCrossLinks
                kicker="Nos prestations"
                title="Terrassement, démolition, génie civil et aménagements extérieurs."
            />

            <SectionCTA
                text="Présentez-nous votre chantier. Nous étudions votre demande de terrassement, démolition, génie civil, voirie, enrobés, aménagement extérieur ou enrochement à Genève ou en Suisse romande, puis revenons vers vous avec une proposition adaptée."
            />
        </>
    );
}

function PageHero({ kicker, h1, lead, paragraphs, image }) {
    return (
        <section className="relative bg-navy-700 text-white overflow-hidden">
            <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-b from-navy-800/80 via-navy-700/70 to-navy-700" />
            <div className="absolute inset-0 bg-topo-dark opacity-40 mix-blend-overlay" />
            <div className="absolute inset-0 noise-overlay" />
            <div className="relative rcps-container pt-24 md:pt-28 pb-16 md:pb-24 max-w-4xl">
                <Reveal>
                    <p className="kicker text-white/80">
                        <span className="hairline bg-white/60" /> {kicker}
                    </p>
                </Reveal>
                <Reveal delay={0.1}>
                    <h1
                        className="mt-4 font-heading text-4xl md:text-6xl font-semibold leading-[1.05] tracking-tight"
                        data-testid="entreprise-h1"
                    >
                        {h1}
                    </h1>
                </Reveal>
                {lead && (
                    <Reveal delay={0.15}>
                        <p className="mt-6 font-heading text-xl md:text-2xl text-white/90 leading-snug tracking-tight max-w-3xl">
                            {lead}
                        </p>
                    </Reveal>
                )}
                {paragraphs && paragraphs.length > 0 && (
                    <div className="mt-6 max-w-2xl space-y-4">
                        {paragraphs.map((p, i) => (
                            <Reveal key={i} delay={0.2 + i * 0.05}>
                                <p className="text-white/80 text-base md:text-lg leading-relaxed">{p}</p>
                            </Reveal>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
